import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { Home, Building2, Settings, DollarSign, Users, Clock, Plus } from "lucide-react";

const GAME_TYPES = ["Spades", "Whist", "Bridge", "Backgammon", "Chess", "Checkers", "Poker", "Monopoly", "Other"];
const SKILL_LEVELS = ["all", "beginner", "intermediate", "advanced"];

const HostGameSection = ({ token, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    game_type: "",
    date: "",
    time: "",
    location: "",
    max_participants: 8,
    entry_fee: 0,
    skill_level: "all",
    rules: "",
    is_tournament: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/events/create`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Event created successfully!");
      setFormData({
        title: "",
        description: "",
        game_type: "",
        date: "",
        time: "",
        location: "",
        max_participants: 8,
        entry_fee: 0,
        skill_level: "all",
        rules: "",
        is_tournament: false,
      });
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create event");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="host-game-section">
      {/* Section Header */}
      <div className="mb-12">
        <h2 className="font-bebas text-4xl md:text-5xl tracking-wider text-white">
          HOST UNFORGETTABLE GAMING EVENTS
        </h2>
        <p className="mt-4 text-[#A1A1AA] font-ubuntu text-lg max-w-3xl">
          Whether you want to host a casual weekly Spades game in your living room or organize a competitive 
          Whist tournament at a local venue, Board2Boston™ provides all the tools you need to create successful gaming events.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Hosting Options */}
        <div className="lg:col-span-1 space-y-6">
          <Card className="bg-[#0A0A0A] border border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                  <Home className="w-5 h-5 text-[#7a6200]" />
                </div>
                <h3 className="font-bebas text-xl tracking-wider text-white">HOSTING AT HOME</h3>
              </div>
              <ul className="space-y-3 text-[#A1A1AA] font-ubuntu text-sm">
                <li className="flex gap-2">
                  <span className="text-[#7a6200]">•</span>
                  Invite-Only Control - Maintain complete privacy
                </li>
                <li className="flex gap-2">
                  <span className="text-[#7a6200]">•</span>
                  Guest Management - Review and approve attendees
                </li>
                <li className="flex gap-2">
                  <span className="text-[#7a6200]">•</span>
                  Custom House Rules - Set your own policies
                </li>
                <li className="flex gap-2">
                  <span className="text-[#7a6200]">•</span>
                  Capacity Management - Control player count
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#7a6200]" />
                </div>
                <h3 className="font-bebas text-xl tracking-wider text-white">PARTNERED VENUES</h3>
              </div>
              <ul className="space-y-3 text-[#A1A1AA] font-ubuntu text-sm">
                <li className="flex gap-2">
                  <span className="text-[#7a6200]">•</span>
                  Curated Venue Selection
                </li>
                <li className="flex gap-2">
                  <span className="text-[#7a6200]">•</span>
                  Built-In Amenities
                </li>
                <li className="flex gap-2">
                  <span className="text-[#7a6200]">•</span>
                  No Setup Hassle
                </li>
                <li className="flex gap-2">
                  <span className="text-[#7a6200]">•</span>
                  Revenue Sharing
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Event Creation Form */}
        <Card className="lg:col-span-2 bg-[#0A0A0A] border border-[#7a6200]/30">
          <CardHeader>
            <CardTitle className="font-bebas text-2xl tracking-wider text-white flex items-center gap-3">
              <Plus className="w-6 h-6 text-[#7a6200]" />
              CREATE NEW EVENT
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Event Title *</label>
                  <Input
                    type="text"
                    placeholder="e.g., Weekly Spades Tournament"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="input-dark h-12"
                    required
                    data-testid="event-title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Game Type *</label>
                  <Select 
                    value={formData.game_type} 
                    onValueChange={(value) => setFormData({ ...formData, game_type: value })}
                  >
                    <SelectTrigger className="input-dark h-12" data-testid="game-type-select">
                      <SelectValue placeholder="Select game type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {GAME_TYPES.map((game) => (
                        <SelectItem key={game} value={game} className="text-white hover:bg-[#7a6200]/20">
                          {game}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Description *</label>
                <Textarea
                  placeholder="Describe your event..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-dark min-h-[100px]"
                  required
                  data-testid="event-description"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Date *</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="input-dark h-12"
                    required
                    data-testid="event-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Time *</label>
                  <Input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="input-dark h-12"
                    required
                    data-testid="event-time"
                  />
                </div>
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Skill Level</label>
                  <Select 
                    value={formData.skill_level} 
                    onValueChange={(value) => setFormData({ ...formData, skill_level: value })}
                  >
                    <SelectTrigger className="input-dark h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {SKILL_LEVELS.map((level) => (
                        <SelectItem key={level} value={level} className="text-white hover:bg-[#7a6200]/20 capitalize">
                          {level === "all" ? "All Levels" : level}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Location *</label>
                <Input
                  type="text"
                  placeholder="Address or venue name"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="input-dark h-12"
                  required
                  data-testid="event-location"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Max Participants</label>
                  <Input
                    type="number"
                    min="2"
                    max="100"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) })}
                    className="input-dark h-12"
                    data-testid="event-max-participants"
                  />
                </div>
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Entry Fee ($)</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.entry_fee}
                    onChange={(e) => setFormData({ ...formData, entry_fee: parseFloat(e.target.value) })}
                    className="input-dark h-12"
                    data-testid="event-entry-fee"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-black/30 rounded border border-white/5">
                <div>
                  <p className="text-white font-ubuntu font-medium">Tournament Mode</p>
                  <p className="text-[#52525B] font-ubuntu text-sm">Enable bracket system and rankings</p>
                </div>
                <Switch
                  checked={formData.is_tournament}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_tournament: checked })}
                  className="data-[state=checked]:bg-[#7a6200]"
                  data-testid="event-tournament-toggle"
                />
              </div>

              <Button
                type="submit"
                className="btn-gold w-full h-14 text-lg"
                disabled={loading}
                data-testid="create-event-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    CREATING EVENT...
                  </span>
                ) : (
                  "CREATE EVENT"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HostGameSection;
